import { useState, useEffect, useRef } from 'react';

// Hook de domaine : DynamicFormModal (états, chargement, navigation, soumission). Iso-comportement.
export const useDynamicForm = ({ slug, backendUrl, initialData = {}, clientId = null, courtierId = null, authToken = null, hideClientFields = true, t, i18n }) => {
  const [formTemplate, setFormTemplate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentCardIndex, setCurrentCardIndex] = useState(0);
  const [navigationHistory, setNavigationHistory] = useState([0]);
  const [formData, setFormData] = useState(initialData);
  const [uploadedFiles, setUploadedFiles] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [isAnimating, setIsAnimating] = useState(false);
  const [animationDirection, setAnimationDirection] = useState('next');
  const [showRecap, setShowRecap] = useState(false);
  const fileInputRefs = useRef({});

  const [useConversationMode, setUseConversationMode] = useState(false);
  const conversationDataRef = useRef({});

  const formLang = i18n.language?.startsWith('en') ? 'en' : 'fr';

  const switchToClassicMode = () => {
    if (conversationDataRef.current && Object.keys(conversationDataRef.current).length > 0) {
      setFormData(prev => ({ ...prev, ...conversationDataRef.current }));
    }
    setUseConversationMode(false);
  };

  const handleConversationSuccess = (submissionId, data) => {
    setSubmitted(true);
  };

  const [clientSearchQuery, setClientSearchQuery] = useState('');
  const [clientSearchResults, setClientSearchResults] = useState([]);
  const [searchingClients, setSearchingClients] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [showClientDropdown, setShowClientDropdown] = useState(false);
  const [showClientCreate, setShowClientCreate] = useState(false);
  const clientSearchRef = useRef(null);

  const [companySearchQuery, setCompanySearchQuery] = useState('');
  const [clientCompanies, setClientCompanies] = useState([]);
  const [filteredCompanies, setFilteredCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [showCompanyDropdown, setShowCompanyDropdown] = useState(false);
  const companySearchRef = useRef(null);

  const getTranslatedText = (frText, enText) => {
    if (formLang === 'en' && enText) {
      return enText;
    }
    return frText || '';
  };

  useEffect(() => {
    loadForm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  useEffect(() => {
    if (clientId && authToken) {
      loadClientCompanies();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, authToken]);

  const loadClientCompanies = async () => {
    try {
      const response = await fetch(`${backendUrl}/api/client/companies`, {
        headers: { 'Authorization': `Bearer ${authToken}` }
      });
      if (response.ok) {
        const data = await response.json();
        setClientCompanies(data.companies || []);
      }
    } catch (error) {
      console.error('Erreur chargement entreprises:', error);
    }
  };

  useEffect(() => {
    if (Object.keys(initialData).length > 0) {
      setFormData(prev => ({ ...initialData, ...prev }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialData]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (clientSearchRef.current && !clientSearchRef.current.contains(event.target)) {
        setShowClientDropdown(false);
      }
      if (companySearchRef.current && !companySearchRef.current.contains(event.target)) {
        setShowCompanyDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const searchClients = async (query) => {
    if (!courtierId || !query || query.length < 2) {
      setClientSearchResults([]);
      return;
    }
    setSearchingClients(true);
    try {
      const response = await fetch(
        `${backendUrl}/api/partner/clients/search?q=${encodeURIComponent(query)}`,
        { headers: { 'Authorization': `Bearer ${authToken}` } }
      );
      if (response.ok) {
        const data = await response.json();
        setClientSearchResults(data.clients || []);
        setShowClientDropdown(true);
      }
    } catch (error) {
      console.error('Erreur recherche clients:', error);
    } finally {
      setSearchingClients(false);
    }
  };

  const selectClient = (client) => {
    setSelectedClient(client);
    setClientSearchQuery(`${client.first_name || ''} ${client.last_name || ''}`.trim());
    setShowClientDropdown(false);
    setFormData(prev => ({
      ...prev,
      field_nom: client.last_name || '',
      field_prenom: client.first_name || '',
      field_email: client.email || '',
      field_telephone: client.phone || client.telephone || '',
      nom: client.last_name || '',
      prenom: client.first_name || '',
      email: client.email || '',
      telephone: client.phone || client.telephone || '',
      selected_client_id: client.id
    }));
  };

  const filterCompanies = (query) => {
    if (!query || query.length < 1) {
      setFilteredCompanies([]);
      setShowCompanyDropdown(false);
      return;
    }
    const filtered = clientCompanies.filter(c =>
      (c.name || '').toLowerCase().includes(query.toLowerCase()) ||
      (c.siret || '').includes(query)
    );
    setFilteredCompanies(filtered);
    setShowCompanyDropdown(filtered.length > 0);
  };

  const selectCompany = (company) => {
    setSelectedCompany(company);
    setCompanySearchQuery(company.name || '');
    setShowCompanyDropdown(false);
    const fullAddress = [company.address, company.postal_code, company.city]
      .filter(Boolean).join(', ');
    setFormData(prev => ({
      ...prev,
      field_entreprise: company.name || '',
      field_siret: company.siret || '',
      field_adresse_pro: fullAddress,
      entreprise: company.name || '',
      nom_entreprise: company.name || '',
      siret: company.siret || '',
      adresse_professionnelle: fullAddress,
      adresse_pro: fullAddress,
      selected_company_id: company.id
    }));
  };

  const shouldHideField = (field) => {
    if (!hideClientFields || !clientId) return false;
    const fieldLabel = (field.label || '').toLowerCase();
    const fieldType = field.type;
    if (fieldType === 'email' || fieldLabel.includes('email')) return true;
    if (fieldType === 'phone' || fieldLabel.includes('téléphone') || fieldLabel.includes('telephone')) return true;
    if (fieldLabel.includes('nom') && !fieldLabel.includes('prénom')) return true;
    if (fieldLabel.includes('prénom') || fieldLabel.includes('prenom')) return true;
    return false;
  };

  const loadForm = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${backendUrl}/api/public/form/${slug}`);
      if (response.ok) {
        const data = await response.json();
        setFormTemplate(data);
      } else {
        setError(t('form.notAvailable'));
      }
    } catch (err) {
      console.error('Erreur chargement formulaire:', err);
      setError(t('form.loading'));
    } finally {
      setLoading(false);
    }
  };

  const updateFormValue = (fieldId, value) => {
    setFormData(prev => ({ ...prev, [fieldId]: value }));
  };

  // Recherche INSEE publique : pré-remplit les sous-champs entreprise d'un champ "company_insee"
  const [siretLookupLoading, setSiretLookupLoading] = useState(null);
  const [siretLookupError, setSiretLookupError] = useState({});
  const lookupCompanySiret = async (fieldId) => {
    const raw = formData[`${fieldId}_siret`] || '';
    const siret = raw.replace(/\s/g, '');
    setSiretLookupError(prev => ({ ...prev, [fieldId]: '' }));
    if (siret.length !== 14 || !/^\d+$/.test(siret)) {
      setSiretLookupError(prev => ({ ...prev, [fieldId]: t('form.siretNeed14') }));
      return;
    }
    try {
      setSiretLookupLoading(fieldId);
      const response = await fetch(`${backendUrl}/api/public/companies/lookup-siret/${siret}`);
      if (response.ok) {
        const data = await response.json();
        setFormData(prev => ({
          ...prev,
          [`${fieldId}_siret`]: data.siret || siret,
          [`${fieldId}_name`]: data.name || '',
          [`${fieldId}_address`]: data.address || '',
          [`${fieldId}_postal_code`]: data.postal_code || '',
          [`${fieldId}_city`]: data.city || '',
          [`${fieldId}_naf`]: data.naf || '',
          [`${fieldId}_effectif`]: data.effectif || '',
        }));
      } else {
        const err = await response.json().catch(() => null);
        setSiretLookupError(prev => ({ ...prev, [fieldId]: err?.detail || t('form.siretLookupError') }));
      }
    } catch (e) {
      setSiretLookupError(prev => ({ ...prev, [fieldId]: t('form.siretLookupError') }));
    } finally {
      setSiretLookupLoading(null);
    }
  };

  const getNextCardIndex = (currentIndex) => {
    const currentCard = formTemplate?.cards?.[currentIndex];
    if (!currentCard?.conditions?.length) {
      return Math.min(currentIndex + 1, (formTemplate?.cards?.length || 1) - 1);
    }
    for (const condition of currentCard.conditions) {
      const fieldValue = formData[condition.source_field_id];
      let matches = false;
      switch (condition.operator) {
        case 'equals':
          matches = fieldValue === condition.value ||
                   (Array.isArray(fieldValue) && fieldValue.includes(condition.value));
          break;
        case 'not_equals':
          matches = fieldValue !== condition.value;
          break;
        case 'contains':
          matches = String(fieldValue || '').toLowerCase().includes(String(condition.value).toLowerCase());
          break;
        case 'greater_than':
          matches = Number(fieldValue) > Number(condition.value);
          break;
        case 'less_than':
          matches = Number(fieldValue) < Number(condition.value);
          break;
        case 'is_empty':
          matches = !fieldValue || (Array.isArray(fieldValue) && fieldValue.length === 0);
          break;
        case 'is_not_empty':
          matches = fieldValue && (!Array.isArray(fieldValue) || fieldValue.length > 0);
          break;
        default:
          break;
      }
      if (matches) {
        if (condition.action === 'go_to_card') {
          const targetIndex = formTemplate.cards.findIndex(c => c.id === condition.target_id);
          if (targetIndex !== -1) return targetIndex;
        } else if (condition.action === 'skip_card') {
          return Math.min(currentIndex + 2, (formTemplate?.cards?.length || 1) - 1);
        }
      }
    }
    return Math.min(currentIndex + 1, (formTemplate?.cards?.length || 1) - 1);
  };

  const calculateRemainingTime = () => {
    if (!formTemplate?.cards) return 0;
    let remainingTime = 0;
    for (let i = currentCardIndex + 1; i < formTemplate.cards.length; i++) {
      remainingTime += formTemplate.cards[i].estimatedTime || 30;
    }
    return remainingTime;
  };

  const formatRemainingTime = (seconds) => {
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    if (secs === 0) return `${minutes} min`;
    return `${minutes} min ${secs}s`;
  };

  const handleFileSelect = (fieldId, e) => {
    const files = Array.from(e.target.files);
    setUploadedFiles(prev => ({
      ...prev,
      [fieldId]: [...(prev[fieldId] || []), ...files]
    }));
    setFormData(prev => ({
      ...prev,
      [fieldId]: [...(prev[fieldId] || []), ...files.map(f => f.name)]
    }));
  };

  const removeFile = (fieldId, index) => {
    setUploadedFiles(prev => ({
      ...prev,
      [fieldId]: (prev[fieldId] || []).filter((_, i) => i !== index)
    }));
    setFormData(prev => ({
      ...prev,
      [fieldId]: (prev[fieldId] || []).filter((_, i) => i !== index)
    }));
  };

  const handleNext = () => {
    const nextIndex = getNextCardIndex(currentCardIndex);
    const enableAnimations = formTemplate?.styles?.enableAnimations !== false;
    if (nextIndex !== currentCardIndex) {
      if (enableAnimations) {
        setAnimationDirection('next');
        setIsAnimating(true);
        setTimeout(() => {
          setNavigationHistory(prev => [...prev, nextIndex]);
          setCurrentCardIndex(nextIndex);
          setTimeout(() => setIsAnimating(false), 50);
        }, 150);
      } else {
        setNavigationHistory(prev => [...prev, nextIndex]);
        setCurrentCardIndex(nextIndex);
      }
    }
  };

  const handlePrevious = () => {
    const enableAnimations = formTemplate?.styles?.enableAnimations !== false;
    if (navigationHistory.length > 1) {
      const newHistory = [...navigationHistory];
      newHistory.pop();
      const previousIndex = newHistory[newHistory.length - 1];
      if (enableAnimations) {
        setAnimationDirection('prev');
        setIsAnimating(true);
        setTimeout(() => {
          setNavigationHistory(newHistory);
          setCurrentCardIndex(previousIndex);
          setTimeout(() => setIsAnimating(false), 50);
        }, 150);
      } else {
        setNavigationHistory(newHistory);
        setCurrentCardIndex(previousIndex);
      }
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const formDataObj = new FormData();
      const mergedFormData = { ...initialData, ...formData };
      const submitData = { formData: mergedFormData };
      if (clientId) {
        submitData.client_id = clientId;
      }
      if (courtierId) {
        submitData.courtier_id = courtierId;
      }
      formDataObj.append('form_data_json', JSON.stringify(submitData));
      Object.keys(uploadedFiles).forEach(fieldId => {
        const files = uploadedFiles[fieldId] || [];
        files.forEach(file => {
          formDataObj.append('files', file);
        });
      });
      const headers = {};
      if (authToken) {
        headers['Authorization'] = `Bearer ${authToken}`;
      }
      const response = await fetch(`${backendUrl}/api/public/form/${slug}/submit`, {
        method: 'POST',
        headers: headers,
        body: formDataObj
      });
      const result = await response.json();
      if (response.ok) {
        setSubmitted(true);
        setSuccessMessage(result.message);
      } else {
        setError(result.detail || 'Erreur lors de l\'envoi');
      }
    } catch (err) {
      console.error('Erreur soumission:', err);
      setError('Erreur lors de l\'envoi');
    } finally {
      setSubmitting(false);
    }
  };

  const isCurrentCardValid = () => {
    const currentCard = formTemplate?.cards?.[currentCardIndex];
    if (!currentCard) return false;
    for (const field of currentCard.fields || []) {
      if (field.type === 'client_search' || field.type === 'company_search') {
        continue;
      }
      if (field.validation?.required) {
        if (field.type === 'file') {
          const files = uploadedFiles[field.id] || [];
          if (files.length === 0) {
            return false;
          }
        } else {
          const value = formData[field.id];
          if (!value || (Array.isArray(value) && value.length === 0)) {
            return false;
          }
        }
      }
    }
    return true;
  };

  return {
    t, clientId,
    formTemplate, loading, error, formLang, getTranslatedText,
    currentCardIndex, setCurrentCardIndex, navigationHistory, setNavigationHistory,
    formData, setFormData, uploadedFiles, fileInputRefs,
    submitting, submitted, successMessage,
    isAnimating, animationDirection,
    showRecap, setShowRecap,
    useConversationMode, setUseConversationMode, conversationDataRef,
    switchToClassicMode, handleConversationSuccess,
    clientSearchQuery, setClientSearchQuery, clientSearchResults, setClientSearchResults, searchingClients,
    selectedClient, setSelectedClient, showClientDropdown, setShowClientDropdown, clientSearchRef,
    showClientCreate, setShowClientCreate,
    companySearchQuery, setCompanySearchQuery, clientCompanies, filteredCompanies,
    setFilteredCompanies, selectedCompany, setSelectedCompany, showCompanyDropdown,
    setShowCompanyDropdown, companySearchRef,
    searchClients, selectClient, filterCompanies, selectCompany, shouldHideField,
    updateFormValue, calculateRemainingTime, formatRemainingTime,
    siretLookupLoading, siretLookupError, lookupCompanySiret,
    backendUrl, authToken, courtierId,
    handleFileSelect, removeFile, handleNext, handlePrevious, handleSubmit, isCurrentCardValid
  };
};
